import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Compass,
  Palette,
  Wrench,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  BarChart3,
  Handshake,
  Building2,
  ClipboardList,
  Rocket,
  LineChart,
} from "lucide-react";
import InquiryForm from "../components/InquiryForm";

const services = [
  {
    id: "positioning",
    n: "01",
    icon: Compass,
    tag: "Serviced-Living Advisory",
    title: "Portfolio Positioning",
    lede: "Reposition a residential asset into a high-yield serviced apartment with editorial appeal.",
    bullets: [
      "Micro-market yield & average-daily-rate modelling",
      "Guest-persona and premium-let strategy",
      "Licensing, insurance & short-let compliance",
    ],
    fee: "Flat mandate · from £2,800 per property",
    image: "/apartments/generated/the-langford-01.jpg",
  },
  {
    id: "interiors",
    n: "02",
    icon: Palette,
    tag: "Interior Curation",
    title: "Design & Staging",
    lede: "Hand-styled interiors, textiles and photography that command the top decile of the market.",
    bullets: [
      "Concept, mood board & specification",
      "Furniture, textiles & art curation",
      "Editorial photography & listing collateral",
    ],
    fee: "Design fee · 8% of FF&E budget",
    image: "/apartments/generated/the-ashbourne-01.jpg",
  },
  {
    id: "operations",
    n: "03",
    icon: Wrench,
    tag: "Managed Hospitality",
    title: "Turnkey Operations",
    lede: "A concierge-led operating layer so absentee owners experience passive, hotel-grade performance.",
    bullets: [
      "Guest concierge, PMS & smart-lock rollout",
      "Housekeeping, linen & maintenance chain",
      "Monthly performance & occupancy reporting",
    ],
    fee: "Retainer · from 15% of net revenue",
    image: "/apartments/generated/the-cavendish-01.jpg",
  },
];

const process = [
  {
    n: "01",
    icon: Handshake,
    title: "Discovery",
    body: "A confidential conversation to understand your asset, ambitions and constraints.",
  },
  {
    n: "02",
    icon: ClipboardList,
    title: "Written Mandate",
    body: "A tailored blueprint with scope, yield model, styling direction and fee schedule.",
  },
  {
    n: "03",
    icon: Rocket,
    title: "Execution",
    body: "We coordinate vendors, contractors and stylists — auditing every milestone on your behalf.",
  },
  {
    n: "04",
    icon: LineChart,
    title: "Ongoing Care",
    body: "Live dashboards, guest care and quarterly portfolio reviews to compound performance.",
  },
];

const pillars = [
  {
    icon: ShieldCheck,
    title: "Aligned incentives",
    body: "Written mandates, no hidden margins. Our success is measured in your yield, not in transactions.",
  },
  {
    icon: Sparkles,
    title: "Editorial standard",
    body: "Every residence is styled and photographed to command the top decile of guest expectations.",
  },
  {
    icon: BarChart3,
    title: "Data-informed",
    body: "Micro-market rate intelligence and occupancy benchmarks guide every recommendation.",
  },
  {
    icon: Building2,
    title: "Hospitality-native",
    body: "Not property management retrofitted — a five-star hospitality practice, from ground up.",
  },
];

export default function Consultancy() {
  return (
    <div data-testid="consultancy-page" className="pb-32">
      {/* HERO — asymmetric editorial split */}
      <section className="container-luxe grid gap-14 pt-16 lg:grid-cols-12 lg:pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          <span className="overline" data-testid="consultancy-overline">
            Sagita Rasmadi · A Hospitality Consultancy
          </span>
          <h1 className="mt-5 font-[Cormorant_Garamond] text-5xl leading-[1.05] tracking-tight text-[#1A1D1A] md:text-6xl lg:text-7xl">
            We manage premium serviced apartments{" "}
            <em className="not-italic text-[#7A8C7B]">on behalf of their owners</em>.
          </h1>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-[#4A524C] md:text-lg">
            A boutique consultancy for private owners, family offices and
            investors who would like their UK residential asset to perform to
            hotel-grade standards — without ever handling a guest, a listing,
            or a cleaner.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#services"
              data-testid="consultancy-cta-services"
              className="btn-primary"
            >
              Explore the Practice
              <ArrowRight size={16} />
            </a>
            <a
              href="#consultancy-inquire"
              data-testid="consultancy-cta-inquire"
              className="btn-secondary"
            >
              Schedule a Conversation
            </a>
          </div>
        </motion.div>

        <motion.aside
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:col-span-5"
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 overflow-hidden rounded-3xl shadow-[0_20px_50px_rgba(26,29,26,0.10)]">
              <div className="aspect-[16/10] bg-[#D9CFC2]">
                <img
                  src="/apartments/generated/the-exchequer-01.jpg"
                  alt="Sagita curated interior"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl">
              <div className="aspect-square bg-[#D9CFC2]">
                <img
                  src="/apartments/generated/the-grosvenor-02.jpg"
                  alt=""
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl">
              <div className="aspect-square bg-[#D9CFC2]">
                <img
                  src="/apartments/generated/the-ashbourne-03.jpg"
                  alt=""
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
          <div className="pointer-events-none absolute -bottom-6 -left-8 hidden rounded-2xl border border-[#D9CFC2] bg-white px-5 py-4 shadow-[0_20px_40px_rgba(26,29,26,0.08)] lg:block">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#C6A66B]">
              Practice Focus
            </div>
            <div className="mt-1 font-[Cormorant_Garamond] text-2xl text-[#1A1D1A]">
              London · UK &amp; select EU
            </div>
          </div>
        </motion.aside>
      </section>

      {/* KPI RIBBON */}
      <section className="mt-20">
        <div className="container-luxe">
          <div className="grid gap-y-8 rounded-3xl border border-[#D9CFC2]/60 bg-white px-8 py-10 shadow-[0_8px_30px_rgba(26,29,26,0.04)] md:grid-cols-4">
            {[
              { k: "£1M – £15M", l: "Typical asset value" },
              { k: "6", l: "Continents · owner geography" },
              { k: "+38%", l: "Avg. yield uplift on repositioned units" },
              { k: "94%", l: "Median occupancy · managed portfolio" },
            ].map((s, i) => (
              <div
                key={s.l}
                className={`text-center md:text-left ${
                  i !== 3 ? "md:border-r md:border-[#D9CFC2]/60 md:pr-8" : ""
                }`}
              >
                <div className="font-[Cormorant_Garamond] text-3xl text-[#1A1D1A] md:text-4xl">
                  {s.k}
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-[#7A8C7B]">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE PRACTICE — alternating editorial rows (DIFFERENT layout from Dira) */}
      <section id="services" className="mt-28">
        <div className="container-luxe">
          <div className="max-w-2xl">
            <span className="overline">The Practice</span>
            <h2 className="mt-4 font-[Cormorant_Garamond] text-4xl text-[#1A1D1A] md:text-5xl">
              Three services. One consistent standard.
            </h2>
            <div className="mt-6 h-px w-12 bg-[#C6A66B]" />
          </div>

          <div className="mt-16 space-y-24">
            {services.map((s, i) => {
              const reverse = i % 2 === 1;
              const Icon = s.icon;
              return (
                <motion.article
                  key={s.id}
                  data-testid={`consultancy-service-${s.id}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className={`grid items-center gap-10 lg:grid-cols-12 lg:gap-16 ${
                    reverse ? "" : ""
                  }`}
                >
                  <div
                    className={`overflow-hidden rounded-3xl shadow-[0_20px_50px_rgba(26,29,26,0.10)] lg:col-span-7 ${
                      reverse ? "lg:order-2" : ""
                    }`}
                  >
                    <div className="aspect-[16/10] bg-[#D9CFC2]">
                      <img
                        src={s.image}
                        alt={s.title}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </div>

                  <div
                    className={`space-y-6 lg:col-span-5 ${
                      reverse ? "lg:order-1" : ""
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="font-[Cormorant_Garamond] text-2xl italic text-[#C6A66B]">
                        {s.n}
                      </span>
                      <span className="overline">{s.tag}</span>
                    </div>
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#C6A66B]/60 text-[#7A8C7B]">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-[Cormorant_Garamond] text-4xl text-[#1A1D1A]">
                      {s.title}
                    </h3>
                    <p className="text-base leading-relaxed text-[#4A524C] md:text-lg">
                      {s.lede}
                    </p>
                    <ul className="space-y-3 pt-2">
                      {s.bullets.map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-3 text-sm text-[#4A524C]"
                        >
                          <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#C6A66B]" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-[#967A45]">
                      <span className="inline-block h-px w-8 bg-[#C6A66B]" />
                      {s.fee}
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW WE ENGAGE — horizontal timeline (different from Dira's vertical) */}
      <section className="mt-32 bg-white py-24">
        <div className="container-luxe">
          <div className="mx-auto max-w-2xl text-center">
            <span className="overline">How We Engage</span>
            <h2 className="mt-4 font-[Cormorant_Garamond] text-4xl text-[#1A1D1A] md:text-5xl">
              A calm, four-step engagement
            </h2>
            <div className="gold-divider mt-6" />
          </div>

          <div className="relative mt-16">
            <div className="pointer-events-none absolute left-8 top-14 hidden h-px w-[calc(100%-4rem)] bg-[#C6A66B]/40 lg:block" />
            <div className="grid gap-10 lg:grid-cols-4">
              {process.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.div
                    key={p.n}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.08 }}
                    className="relative"
                  >
                    <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[#C6A66B]/60 bg-white text-[#7A8C7B] shadow-[0_4px_18px_rgba(26,29,26,0.04)]">
                      <Icon size={20} />
                    </div>
                    <div className="mt-6 font-[Cormorant_Garamond] text-3xl italic text-[#C6A66B]">
                      {p.n}
                    </div>
                    <h4 className="mt-2 font-[Cormorant_Garamond] text-2xl text-[#1A1D1A]">
                      {p.title}
                    </h4>
                    <p className="mt-3 text-sm leading-relaxed text-[#4A524C]">
                      {p.body}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="mt-24">
        <div className="container-luxe">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <span className="overline">Why Sagita Consultancy</span>
              <h2 className="mt-4 font-[Cormorant_Garamond] text-4xl text-[#1A1D1A] md:text-5xl">
                Practised, not promised.
              </h2>
              <div className="mt-6 h-px w-12 bg-[#C6A66B]" />
              <p className="mt-6 text-base leading-relaxed text-[#4A524C]">
                We built the operating standard by looking after client-owned
                residences every day — every recommendation is battle-tested
                inside a live mandate before it reaches yours.
              </p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:col-span-8">
              {pillars.map((p) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.title}
                    className="rounded-2xl border border-[#D9CFC2]/60 bg-white p-7 shadow-[0_8px_30px_rgba(26,29,26,0.04)] transition-all hover:shadow-[0_20px_40px_rgba(26,29,26,0.08)]"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F7F4ED] text-[#7A8C7B]">
                      <Icon size={18} />
                    </div>
                    <h4 className="mt-5 font-[Cormorant_Garamond] text-2xl text-[#1A1D1A]">
                      {p.title}
                    </h4>
                    <p className="mt-3 text-sm leading-relaxed text-[#4A524C]">
                      {p.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CLIENT VOICE / PULL QUOTE */}
      <section className="mt-24">
        <div className="container-luxe">
          <div className="relative overflow-hidden rounded-3xl bg-[#7A8C7B] px-8 py-16 text-white md:px-16 md:py-24">
            <div className="absolute -top-16 -left-6 font-[Cormorant_Garamond] text-[280px] leading-none text-white/10">
              &ldquo;
            </div>
            <div className="relative max-w-3xl">
              <p className="font-[Cormorant_Garamond] text-3xl italic leading-tight md:text-4xl">
                Sagita reframed our two Kensington units from tired long-lets
                into flagship serviced residences. The design, the numbers, and
                the guests all followed.
              </p>
              <div className="mt-8 flex items-center gap-4 text-xs uppercase tracking-[0.24em] text-white/80">
                <span className="inline-block h-px w-8 bg-[#C6A66B]" />
                Private Owner · London SW7
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INQUIRY */}
      <section id="consultancy-inquire" className="mt-24">
        <div className="container-luxe grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="overline">Begin a conversation</span>
            <h2 className="mt-4 font-[Cormorant_Garamond] text-4xl text-[#1A1D1A] md:text-5xl">
              Speak with the practice.
            </h2>
            <div className="mt-6 h-px w-12 bg-[#C6A66B]" />
            <p className="mt-6 text-base leading-relaxed text-[#4A524C]">
              Every mandate begins with a written brief. Send a short note and
              our practice lead will respond within one business day.
              Conversations are confidential and there is no fee for the
              initial discovery call.
            </p>
            <div className="mt-8 space-y-2 text-sm text-[#4A524C]">
              <div className="inline-flex items-center gap-2">
                <span className="h-px w-6 bg-[#C6A66B]" />
                Consultancy inquiries · info@sagita-llc.com
              </div>
              <div className="inline-flex items-center gap-2">
                <span className="h-px w-6 bg-[#C6A66B]" />
                Direct line · +1 866 803 7635
              </div>
              <div className="inline-flex items-start gap-2">
                <span className="mt-2 h-px w-6 flex-shrink-0 bg-[#C6A66B]" />
                <span>Registered Office · 30 N Gould St Ste R, Sheridan, WY 82801, USA</span>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-[0.22em] text-[#7A8C7B]">
                <span>Owners</span>
                <span className="h-1 w-1 rounded-full bg-[#C6A66B]" />
                <span>Family Offices</span>
                <span className="h-1 w-1 rounded-full bg-[#C6A66B]" />
                <span>Boutique Operators</span>
              </div>
            </div>

            <Link
              to="/portfolio"
              data-testid="consultancy-see-work"
              className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#7A8C7B] hover:text-[#1A1D1A]"
            >
              See selected case studies
              <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <InquiryForm />
          </div>
        </div>
      </section>
    </div>
  );
}
